import { useMemo } from "react";

import { Input } from "../../../components/Input/Input";
import { Select, type Option } from "../../../components/Select/Select";
import type { GiftClaimMode, GiftType } from "./types";
import { humanizeDuration } from "./utils";
import {
  AMOUNT_MAX,
  AMOUNT_MIN,
  MAX_MAX,
  MAX_MIN,
  TYPE_REGEX,
  WINDOW_MAX,
  WINDOW_MIN,
  amountError,
  limitError,
  pairLimitError,
} from "./giftTypes";
// The table's silhouette, borrowed whole from the section this one sits under.
// Only the six-column track list and the pair sub-row are gift-shaped, and
// those are the two blocks the mockup marks as new pixels.
import grid from "./HelpTypesEditor.module.css";
import styles from "./GiftTypesEditor.module.css";

/** "One at a time" leads, as in the helper line under the editor. */
const CLAIM_OPTIONS: Option[] = [
  { value: "one_at_a_time", label: "One at a time" },
  { value: "all_at_once", label: "All at once" },
];

/**
 * What the small line under a window box says: the window in words, or that
 * there is no window at all.
 *
 * The same `humanizeDuration` that reads out the section's Unclaimed lifetime,
 * so one card spells a duration one way. An empty box is a state and not a gap
 * — "No limit" is the answer, where a blank cell would leave the operator to
 * guess whether they had typed something wrong.
 */
function windowNote(seconds: number | undefined): string {
  return seconds === undefined ? "No limit" : `= ${humanizeDuration(seconds)}`;
}

interface Props {
  value: GiftType[];
  onChange: (next: GiftType[]) => void;
}

/**
 * `gift.types` — one row per gift type: its name, the mode by which the
 * recipient takes it out of their inbox, how often the sender may give it away,
 * how much may ride on one send, and how often the same pair of players may
 * repeat it.
 *
 * Mockup: .codelane/artifacts/t-125/config-gifts.html L356-L436. A table rather
 * than several lists because every one of these is a property OF the type: a
 * separate "types with a pair limit" list would be two things obliged to agree,
 * and a row makes disagreement inexpressible. That is the argument
 * `HelpTypesEditor` already records, and this is the same table with a column
 * taken away and a quota added.
 *
 * Errors follow that editor too: the offending row colours its own boxes and
 * says why, while `giftTypesError` lifts the same rules to the save bar.
 */
export function GiftTypesEditor({ value, onChange }: Props) {
  // Which names collide. Case-sensitive — TYPE_REGEX admits lowercase only.
  const duplicates = useMemo(() => {
    const seen = new Set<string>();
    const dups = new Set<string>();
    for (const row of value) {
      if (!row.type) continue;
      if (seen.has(row.type)) dups.add(row.type);
      seen.add(row.type);
    }
    return dups;
  }, [value]);

  const patch = (idx: number, next: Partial<GiftType>) =>
    onChange(value.map((row, i) => (i === idx ? { ...row, ...next } : row)));

  type NumberKey =
    | "max_per_window"
    | "window_seconds"
    | "max_amount"
    | "pair_max_per_window"
    | "pair_window_seconds";

  /**
   * Write one of the row's optional numbers as a VALUE. `undefined` DELETES the
   * key rather than storing 0 or NaN: "not set" is the absence of the key — no
   * limit and no cap — and a row that arrived without one has to serialise back
   * without it.
   */
  const patchValue = (idx: number, key: NumberKey, next: number | undefined) =>
    onChange(
      value.map((row, i) => {
        if (i !== idx) return row;
        const out = { ...row };
        if (next === undefined) delete out[key];
        else out[key] = next;
        return out;
      }),
    );

  /** The same write from what the operator TYPED: an emptied or half-typed box
   *  is "not set". */
  const patchNumber = (idx: number, key: NumberKey, raw: string) => {
    const n = Number(raw);
    patchValue(idx, key, raw === "" || !Number.isFinite(n) ? undefined : n);
  };

  return (
    <>
      <div className={grid.gridEditor}>
        <div className={grid.scroller}>
          <div className={`${grid.row} ${styles.types} ${grid.head}`}>
            <div>gift_type</div>
            <div>claim mode</div>
            <div>max</div>
            <div>per window</div>
            <div>max amount</div>
            <div />
          </div>
          {value.map((row, idx) => {
            // A row the operator has not named yet is not yet a mistake — the
            // save bar still refuses it, but the box stays quiet.
            const name = row.type || `type ${idx + 1}`;
            const error = !row.type
              ? undefined
              : duplicates.has(row.type)
                ? "Duplicate type"
                : !TYPE_REGEX.test(row.type)
                  ? "Use lowercase letters, digits, underscore"
                  : undefined;
            const limit = limitError(row);
            // Half a pair marks BOTH boxes: neither half means anything on its
            // own, so neither may look like the settled one.
            const maxBad = !!(limit?.pair || limit?.max);
            const windowBad = !!(limit?.pair || limit?.window);
            const windowMsg = limit?.pair ?? limit?.window;
            const amountBad = amountError(row);
            // The per-recipient quota is read by the same rules and painted the
            // same way — one pair of boxes lower down the row.
            const pair = pairLimitError(row);
            const pairMaxBad = !!(pair?.pair || pair?.max);
            const pairWindowBad = !!(pair?.pair || pair?.window);
            const pairWindowMsg = pair?.pair ?? pair?.window;
            return (
              <div
                key={idx}
                className={`${grid.row} ${styles.types} ${grid.tall}`}
              >
                <div className={grid.cell}>
                  <Input
                    className={grid.monoInput}
                    value={row.type}
                    onChange={(e) => patch(idx, { type: e.target.value })}
                    placeholder="gift_type"
                    aria-label={`Type ${idx + 1} name`}
                    error={error}
                  />
                </div>
                <div className={grid.cell}>
                  <Select
                    className={grid.claimSelect}
                    value={row.claim}
                    onChange={(v) => patch(idx, { claim: v as GiftClaimMode })}
                    options={CLAIM_OPTIONS}
                    aria-label={`${name} claim mode`}
                  />
                </div>
                <div className={grid.cell}>
                  <Input
                    className={maxBad ? grid.err : undefined}
                    type="number"
                    min={MAX_MIN}
                    max={MAX_MAX}
                    value={row.max_per_window ?? ""}
                    onChange={(e) =>
                      patchNumber(idx, "max_per_window", e.target.value)
                    }
                    placeholder="∞"
                    aria-label={`${name} max per window`}
                    aria-invalid={maxBad || undefined}
                  />
                  {limit?.max && <div className={grid.errText}>{limit.max}</div>}
                </div>
                <div className={grid.cell}>
                  <div className={grid.unitWrap}>
                    <Input
                      className={`${grid.unitInput} ${windowBad ? grid.err : ""}`}
                      type="number"
                      min={WINDOW_MIN}
                      max={WINDOW_MAX}
                      value={row.window_seconds ?? ""}
                      onChange={(e) =>
                        patchNumber(idx, "window_seconds", e.target.value)
                      }
                      placeholder="—"
                      aria-label={`${name} window seconds`}
                      aria-invalid={windowBad || undefined}
                      trailingIcon={
                        <span className={grid.unit} aria-hidden>
                          sec
                        </span>
                      }
                    />
                  </div>
                  {/* Half a pair says why ONCE, under the window — two copies of
                      one sentence would read as two problems. */}
                  {windowMsg ? (
                    <div className={grid.errText}>{windowMsg}</div>
                  ) : (
                    <div className={grid.mini}>
                      {windowNote(row.window_seconds)}
                    </div>
                  )}
                </div>
                <div className={grid.cell}>
                  <Input
                    className={amountBad ? grid.err : undefined}
                    type="number"
                    min={AMOUNT_MIN}
                    max={AMOUNT_MAX}
                    value={row.max_amount ?? ""}
                    onChange={(e) =>
                      patchNumber(idx, "max_amount", e.target.value)
                    }
                    // The same ∞ the quota's max wears when it is empty: both
                    // boxes mean "nothing stops this" when nothing is in them.
                    placeholder="∞"
                    aria-label={`${name} max amount`}
                    aria-invalid={!!amountBad || undefined}
                  />
                  {amountBad && <div className={grid.errText}>{amountBad}</div>}
                </div>
                <button
                  type="button"
                  className={grid.rowBtn}
                  aria-label={`Remove ${name}`}
                  onClick={() => onChange(value.filter((_, i) => i !== idx))}
                >
                  ✕
                </button>
                {/* The per-recipient quota is a property OF the type like
                    everything above it, but it is a second pair of boxes — so
                    it takes the row's full width on a second line instead of
                    two more columns that would set every project's table
                    scrolling sideways (mockup L203-L208). */}
                <div className={styles.pairSub}>
                  <div className={styles.pairLabel}>to one player</div>
                  <div className={styles.pairFields}>
                    <div className={`${grid.cell} ${styles.pairMax}`}>
                      <Input
                        className={pairMaxBad ? grid.err : undefined}
                        type="number"
                        min={MAX_MIN}
                        max={MAX_MAX}
                        value={row.pair_max_per_window ?? ""}
                        onChange={(e) =>
                          patchNumber(
                            idx,
                            "pair_max_per_window",
                            e.target.value,
                          )
                        }
                        placeholder="∞"
                        aria-label={`${name} pair max per window`}
                        aria-invalid={pairMaxBad || undefined}
                      />
                      {pair?.max && (
                        <div className={grid.errText}>{pair.max}</div>
                      )}
                    </div>
                    <span className={styles.pairWord}>per</span>
                    <div className={grid.cell}>
                      <div className={grid.unitWrap}>
                        <Input
                          className={`${grid.unitInput} ${pairWindowBad ? grid.err : ""}`}
                          type="number"
                          min={WINDOW_MIN}
                          max={WINDOW_MAX}
                          value={row.pair_window_seconds ?? ""}
                          onChange={(e) =>
                            patchNumber(
                              idx,
                              "pair_window_seconds",
                              e.target.value,
                            )
                          }
                          placeholder="—"
                          aria-label={`${name} pair window seconds`}
                          aria-invalid={pairWindowBad || undefined}
                          trailingIcon={
                            <span className={grid.unit} aria-hidden>
                              sec
                            </span>
                          }
                        />
                      </div>
                      {pairWindowMsg ? (
                        <div className={grid.errText}>{pairWindowMsg}</div>
                      ) : (
                        <div className={grid.mini}>
                          {windowNote(row.pair_window_seconds)}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
        <button
          type="button"
          className={grid.addRow}
          onClick={() => onChange([...value, { type: "", claim: "all_at_once" }])}
        >
          + Add type
        </button>
      </div>
      <div className={grid.helper}>
        <b>One at a time</b> — the recipient may take part of a gift and leave
        the rest for later (lives against a cap). <b>All at once</b> — the gift
        is taken whole, in one call. The same two modes the inbox already reads
        off a help donation, and read from the live config at claim time.
      </div>
      <div className={grid.helper}>
        <b>max / per window</b> — how many gifts of this type one player may send
        over the last window, counted back from now, to everybody together. Leave
        both empty for no limit. Every send counts, whether or not it was ever
        taken.
      </div>
      <div className={grid.helper}>
        <b>max amount</b> — how much may ride on one send. Leave empty for no
        cap.
      </div>
      <div className={grid.helper}>
        <b>to one player</b> — the same quota narrowed to one pair of players:
        how often the sender may give this type to the <i>same</i> recipient.
        This is the rule that stops one player being buried by one other. Leave
        both empty for no limit.
      </div>
    </>
  );
}
